import { QueryClientProvider } from '@tanstack/react-query';
import { Component, type ReactNode, lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ConsentPanel } from '@/components/ConsentLayer';
import { queryClient, setupPersistence } from '@/lib/api';
import { MapPage } from '@/pages/MapPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { RulesPage } from '@/pages/RulesPage';

// The admin console is never needed by a visitor, and it pulls in a second
// MapLibre instance for the moderation previews. Split it out of the bundle
// the public actually downloads.
const AdminPage = lazy(() =>
  import('@/pages/AdminPage').then((m) => ({ default: m.AdminPage })),
);

setupPersistence();

/**
 * The consent UI must never be able to take the map down with it. Anything it
 * throws is contained here and logged; the map keeps rendering.
 */
class ConsentBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    console.error('[consent] potlačeno, mapa běží dál:', error);
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<MapPage />} />
          <Route path="/soukromi" element={<PrivacyPage />} />
          <Route path="/pravidla" element={<RulesPage />} />
          <Route
            path="/admin"
            element={
              <Suspense fallback={<p className="p-6 text-slate-600">Načítám administraci…</p>}>
                <AdminPage />
              </Suspense>
            }
          />
          <Route path="*" element={<MapPage />} />
        </Routes>
        <ConsentBoundary>
          <ConsentPanel />
        </ConsentBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
